import { Button, SelectInput, SelectInputItem, TextField } from '@ads/components-react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ECompanyFilterType } from '@/shared/api/generated';

import { SERIE_COMPANY_FILTER_TYPE } from '../../../types/serieFilters';
import { CompanyAutocomplete } from '../CompanyAutocomplete';

/** Parâmetros de busca aplicados ao clicar em "Pesquisar" (ou Enter). */
export interface SerieSearchParams {
  readonly search: string;
  readonly filter: ECompanyFilterType;
  readonly companyId: number | undefined;
}

export interface SerieSearchBarProps {
  /** Texto de busca já aplicado (usado para inicializar o campo). */
  readonly defaultValue: string;
  /** Filtro de origem já aplicado (usado para inicializar o dropdown). */
  readonly defaultFilter: ECompanyFilterType;
  /** Empresa já aplicada (usada para inicializar o autocomplete, quando `defaultFilter === Company`). */
  readonly defaultCompanyId: number | undefined;
  /** Acionado ao pesquisar (Enter ou botão), com o texto, o filtro de origem e a empresa atuais. */
  readonly onSearch: (params: SerieSearchParams) => void;
  /** Acionado ao limpar todos os filtros (texto, origem e empresa). */
  readonly onClear: () => void;
}

/**
 * Barra de busca da listagem de Séries. Mantém texto, filtro de origem
 * (Acadêmico Pecege/Empresa/Todos) e empresa pendentes localmente e só aplica
 * ao acionar "Pesquisar" (ou Enter), acionando `onSearch` — nada dispara busca
 * sozinho ao trocar o dropdown ou selecionar a empresa. A busca é
 * **server-side** (`GET /api/v1/serie?search=&filter=&companyId=`), diferente
 * do padrão client-side usado em Localização.
 */
export function SerieSearchBar({
  defaultValue,
  defaultFilter,
  defaultCompanyId,
  onSearch,
  onClear,
}: SerieSearchBarProps) {
  const { t } = useTranslation('series');
  const [pendingSearch, setPendingSearch] = useState(defaultValue);
  const [pendingFilter, setPendingFilter] = useState<ECompanyFilterType>(defaultFilter);
  const [pendingCompanyId, setPendingCompanyId] = useState<number | undefined>(defaultCompanyId);
  const [pendingCompanyLabel, setPendingCompanyLabel] = useState<string | undefined>(undefined);

  const isCompanyFilter = pendingFilter === SERIE_COMPANY_FILTER_TYPE.Company;

  const handleFilterChange = (filter: ECompanyFilterType) => {
    setPendingFilter(filter);
    if (filter !== SERIE_COMPANY_FILTER_TYPE.Company) {
      setPendingCompanyId(undefined);
      setPendingCompanyLabel(undefined);
    }
  };

  const handleSearch = () =>
    onSearch({
      search: pendingSearch.trim(),
      filter: pendingFilter,
      companyId: isCompanyFilter ? pendingCompanyId : undefined,
    });

  const handleClear = () => {
    setPendingSearch('');
    setPendingFilter(SERIE_COMPANY_FILTER_TYPE.All);
    setPendingCompanyId(undefined);
    setPendingCompanyLabel(undefined);
    onClear();
  };

  return (
    <div className="rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs shadow-level1">
      <form
        className="flex flex-col items-start gap-micro"
        onSubmit={e => {
          e.preventDefault();
          handleSearch();
        }}
      >
        <div className="flex w-full flex-wrap items-end gap-mini">
          <div className="min-w-[220px] flex-[2]">
            <TextField
              label={t('series.search.label')}
              placeholder={t('series.search.placeholder')}
              value={pendingSearch}
              size="md"
              fullWidth
              leadingIcon={MagnifyingGlassIcon}
              onChange={e => setPendingSearch(e.target.value)}
            />
          </div>
          <div className="min-w-[180px] flex-1">
            <SelectInput
              label={t('series.search.filterType.label')}
              value={String(pendingFilter)}
              onValueChange={v => handleFilterChange(Number(v) as ECompanyFilterType)}
              size="md"
              fullWidth
            >
              <SelectInputItem
                value={String(SERIE_COMPANY_FILTER_TYPE.Main)}
                custom={{ root: 'text-nowrap' }}
              >
                {t('series.search.filterType.main')}
              </SelectInputItem>
              <SelectInputItem
                value={String(SERIE_COMPANY_FILTER_TYPE.Company)}
                custom={{ root: 'text-nowrap' }}
              >
                {t('series.search.filterType.company')}
              </SelectInputItem>
              <SelectInputItem
                value={String(SERIE_COMPANY_FILTER_TYPE.All)}
                custom={{ root: 'text-nowrap' }}
              >
                {t('series.search.filterType.all')}
              </SelectInputItem>
            </SelectInput>
          </div>

          {isCompanyFilter && (
            <div className="min-w-[220px] flex-1">
              <CompanyAutocomplete
                value={pendingCompanyId ?? null}
                {...(pendingCompanyLabel !== undefined
                  ? { selectedLabel: pendingCompanyLabel }
                  : {})}
                onChange={(id, label) => {
                  setPendingCompanyId(id ?? undefined);
                  setPendingCompanyLabel(label);
                }}
                label={t('series.search.company.label')}
                placeholder={t('series.search.company.placeholder')}
              />
            </div>
          )}
        </div>

        <div className="flex items-center gap-nano">
          <Button type="submit" hierarchy="primary" size="md" leadingIcon={MagnifyingGlassIcon}>
            {t('series.search.button')}
          </Button>
          <Button type="button" hierarchy="secondary" size="md" onClick={handleClear}>
            {t('series.search.clear')}
          </Button>
        </div>
      </form>
    </div>
  );
}
